// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O₂ · QUEM MAIS ESTÁ NO PLANO — o sinal, e o limite dele escrito ao lado
// ============================================================================
//
// 📗 `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`, §11 O, ponto 2:
//    *«um sinal discreto de quem mais está no plano»*.
//
// ⚠️⚠️ **A medição que fixou as palavras está em `apps/api/src/presenca.ts` §2, e lê-se
//    primeiro.** Em duas linhas: a `sessao` sabe quem **entrou** e que plano escolheu, não sabe
//    se está ao ecrã, e dura 12 h sem deslizar. Medido no `dev` a 2026-09-16 — três sessões
//    vivas, **uma só pessoa**, com 4,6 a 5,0 h de idade e a última escrita dela há **73 min**.
//    Um sinal que dissesse «está a planear» estaria a afirmar o que não sabe.
//
// ---------------------------------------------------------------------------
// AS ESCOLHAS, uma a uma
// ---------------------------------------------------------------------------
//
//   · **Cala-se quando está sozinho.** Um «0 com o plano aberto» permanente é decoração, e
//     decoração num cabeçalho ensina a não o ler. O sinal só existe quando há alguém.
//
//   · **Não acrescenta linha à barra**, que está no tecto de 150 px
//     (`nada-e-cortado-pela-janela.test.tsx`). Vive na faixa `.topo-metricas`, que já existe e
//     já hospeda o `PlanoMudou` e os KPI, pela mesma razão: quem está a decidir sobre a viatura
//     108 tem de continuar a vê-la, logo nada empurra a grelha para baixo.
//
//   · **Custo em nós, medido pela forma e não por estimativa:** 1 `<details>` + 1 `<summary>`
//     com 2 filhos, mais **3 nós por pessoa** dentro da lista — e a lista só existe depois de
//     alguém a abrir. Com as cinco contas que a base tem, o pior caso são ~20 nós **uma vez**,
//     contra os 3 889 do mapa. ⚠️ **Não é por linha de viatura:** um sinal por linha eram +250
//     nós, e o tecto de 1 400 células por janela não se toca.
//
//   · **Nunca bloqueia nada** (ADR-004). A sonda falha → o sinal desaparece e o mapa continua
//     igual. Não há estado de erro: um erro sobre «quem está aqui» é ruído sobre uma coisa que
//     não impede ninguém de trabalhar.
//
//   · **`aria-live` e não `role="status"`** — o cabeçalho já tem dois elementos com esse papel
//     (`.filtro-activo`, `.topo-medidas`) e um terceiro parte provas que procuram «o» status. É
//     a mesma decisão do `PlanoMudou`, e pela mesma razão medida.
//
// ⚠️ **REGRA 7:** isto mostra nomes de colegas. É uso interno e legítimo — é a razão de ser da
//    peça. **Mas nenhum nome real entra em fixture, prova ou captura.**
//
// 🅿️🔴 **O que este sinal NÃO faz, e o próximo tem de ler antes de tentar:** não diz em que
//    viatura nem em que dias a outra pessoa está — e **não se adivinha daqui**. Isso é o
//    **mínimo 3** de §11 O (*«ao gravar, se aquelas linhas mudaram desde que as li»*), que
//    ficou **parado com a razão medida**: a formulação já existe (`posterioresQueTocam`, em
//    `desfazer-selectivo.ts`) e o que falta é a **âncora** — `alocacao` não tem coluna de
//    versão, e `auditoria.entidade_id` tem quatro formas, logo ~19 % das linhas não se lêem
//    como (viatura, dias). **A saída honesta é uma migração, e é decisão do cliente.** A
//    medição completa está na linha **O** da tabela de
//    `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`.
// ============================================================================

import { useEffect, useState } from 'react';

/**
 * De quanto em quanto tempo se pergunta quem está no plano.
 *
 * ⚠️ **Mais lento do que a sonda do `PlanoMudou` (30 s) de propósito, e não por economia:** o
 * que ali se mede muda a cada escrita de qualquer pessoa; aqui muda quando alguém **entra ou
 * sai**, que é ordens de grandeza mais raro. Uma sonda mais rápida do que o facto que mede
 * gasta rede para repetir a mesma resposta.
 */
export const INTERVALO_PRESENCA_MS = 60_000;

/** A forma que `GET /api/plano/presenca` devolve — ver `apps/api/src/presenca.ts`. */
export interface PessoaNoPlanoNoEcra {
  readonly utilizadorId: string;
  readonly nome: string;
  readonly sessoes: number;
  readonly entrouHaMin: number;
  /** ⚠️ `null` = **não escreveu**, e não «escreveu agora». Ver o §2 do módulo da API. */
  readonly escreveuHaMin: number | null;
}

export interface RespostaDaPresenca {
  readonly pessoas: readonly PessoaNoPlanoNoEcra[];
  readonly semPlano: boolean;
  readonly janelaH: number;
}

/** Minutos legíveis. ⚠️ Acima de duas horas o minuto deixa de informar. */
function minutos(m: number): string {
  if (m < 1) return 'menos de 1 min';
  if (m < 120) return `${String(m)} min`;
  return `${String(Math.round(m / 60))} h`;
}

/**
 * A linha de uma pessoa: os **dois factos separados**, nunca fundidos.
 *
 * ⚠️⚠️ «entrou há» é o que a `sessao` sabe; «escreveu há» é o que a `auditoria` prova. Fundi-los
 * numa só frase obrigava a que a mais fraca mandasse — e a mais fraca é a presença.
 */
export function fraseDaPessoa(p: PessoaNoPlanoNoEcra): string {
  const janelas = p.sessoes > 1 ? ` · ${String(p.sessoes)} janelas` : '';
  const entrou = `entrou há ${minutos(p.entrouHaMin)}`;
  return p.escreveuHaMin === null
    ? `${entrou} · sem escritas neste plano${janelas}`
    : `${entrou} · escreveu há ${minutos(p.escreveuHaMin)}${janelas}`;
}

export interface QuemEstaNoPlanoProps {
  /** Existe para as provas não esperarem 60 s. Em produção nunca se passa. */
  readonly intervaloMs?: number;
  /**
   * ⚠️ **Não é dado: é o gatilho.** Um `sinal` novo (o `dados` do mapa) quer dizer «houve uma
   * recarga», e é a altura em que vale a pena voltar a perguntar sem esperar pelo temporizador.
   */
  readonly sinal?: unknown;
}

export function QuemEstaNoPlano({
  intervaloMs = INTERVALO_PRESENCA_MS,
  sinal,
}: QuemEstaNoPlanoProps) {
  const [pessoas, definirPessoas] = useState<readonly PessoaNoPlanoNoEcra[]>([]);

  useEffect(() => {
    const abortar = new AbortController();
    let aCorrer = false;

    const sondar = async (): Promise<void> => {
      if (aCorrer) return;
      aCorrer = true;
      try {
        const r = await fetch('/api/plano/presenca', {
          cache: 'no-store',
          signal: abortar.signal,
        });
        if (!r.ok) {
          // ⚠️ 401, 403, 503 — nenhum diz «não há ninguém». Cala-se, que é a resposta honesta,
          //    e o mapa não sente nada (ADR-004).
          definirPessoas([]);
          return;
        }
        const c = (await r.json()) as RespostaDaPresenca;
        definirPessoas(c.semPlano ? [] : (c.pessoas ?? []));
      } catch {
        definirPessoas([]);
      } finally {
        aCorrer = false;
      }
    };

    void sondar();
    const temporizador = window.setInterval(() => void sondar(), intervaloMs);
    return () => {
      window.clearInterval(temporizador);
      abortar.abort();
    };
  }, [intervaloMs, sinal]);

  // ⚠️ A região viva existe SEMPRE e o conteúdo é que aparece — um `aria-live` que só nasce com
  //    o texto lá dentro não é anunciado por leitor de ecrã nenhum. Mesma razão do `PlanoMudou`.
  return (
    <span className="quem-no-plano" aria-live="polite">
      {pessoas.length === 0 ? null : (
        <details className="quem-no-plano-cx">
          <summary title="quem mais tem este plano aberto — ter aberto não é estar a mexer">
            <span className="quem-no-plano-pt" aria-hidden="true" />
            {pessoas.length === 1
              ? '1 com o plano aberto'
              : `${String(pessoas.length)} com o plano aberto`}
          </summary>
          <div className="quem-no-plano-gaveta">
            <ul className="quem-no-plano-lista">
              {pessoas.map((p) => (
                <li key={p.utilizadorId}>
                  <strong>{p.nome}</strong>
                  <span className="quem-no-plano-detalhe">{fraseDaPessoa(p)}</span>
                </li>
              ))}
            </ul>
            {/* ⚠️⚠️ **O LIMITE DIZ-SE, e fica DENTRO da gaveta** — é a regra dos três sítios da
                maqueta: a contagem no cabeçalho, a lista atrás do triângulo, a ressalva junto do
                que ela qualifica. Escondê-la fazia o sinal afirmar mais do que sabe; pô-la na
                faixa fazia uma frase de aviso viver onde vivem números. */}
            <p className="quem-no-plano-limite">
              Ter o plano aberto <strong>não é</strong> estar a mexer nele — uma sessão dura
              horas. A única prova de acção é a última escrita, acima.
            </p>
          </div>
        </details>
      )}
    </span>
  );
}
